/**
 * Edit Tenant: switch a product's optional ADD-ONS on a tenant that exists.
 *
 * Engage today (owner product model, 27 Sep 2026): Forms and Prospect
 * Capture come with Engage and are shown as read-only ticks; Surveys and
 * Landing Pages are optional add-ons, each with its own price. Same picture
 * as Create Tenant (the same `IncludedFeatureTicks`, fed by the same server
 * rows), applied to a live tenant through its own endpoint:
 *
 *   GET /api/admin/tenants/{id}/product-addons         what can be switched
 *   PUT /api/admin/tenants/{id}/product-addons/{code}  {enabled}
 *
 * Kept OUT of the Edit Tenant form's Save, like ProductEditionSection: that
 * Save PATCHes profile fields; switching an add-on changes what the tenant may
 * use. It is its own confirmed action, so renaming a tenant can never switch
 * Surveys off by accident.
 *
 * An add-on can be switched ON only while the tenant holds its product (the
 * server says so as `held`; the toggle is disabled otherwise and the server
 * refuses regardless). Switching one OFF says what that means BEFORE it is
 * applied: access is removed, nothing is deleted. The agreement is not
 * re-priced; the result says it needs amending to bill the change.
 *
 * THE BUTTON NAMES THE ACTION, FROM THE TENANT'S CURRENT STATE. Surveys on →
 * "Remove Surveys"; off → "Add Surveys". Clicking it (or the checkbox) stages
 * that change and the button becomes "Confirm: remove Surveys" beside a
 * Cancel; only that second click calls the server. It used to follow the
 * checkbox alone, so an add-on that was already on sat under a greyed-out
 * "Add Surveys" (DEV Admin, 27 Sep 2026).
 *
 * AN UNPRICED ADD-ON SAYS WHERE ITS PRICE GOES. It read "No price set", as if
 * that were what Surveys is (DEV, 4 Oct 2026). The server now says it is not
 * in the default price book, names that book, and hands over the remedy; the
 * row links to Base Prices opened on the add-on (new tab) and "Check again"
 * re-reads this list, so the price appears here without closing the dialog.
 * Switching an add-on never needs a price — billing it does.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import { IncludedFeatureTicks, PriceRemedy, addonPriceText } from './ProductAddons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_API = `${BACKEND_URL}/api/admin`;

/** The add-on button's words: the action from the current state, or the
 * staged change awaiting confirmation. */
export function actionLabel({ saving, dirty, value, enabled, label }) {
  if (saving) return 'Applying…';
  if (dirty) return value ? `Confirm: add ${label}` : `Confirm: remove ${label}`;
  return enabled ? `Remove ${label}` : `Add ${label}`;
}

function errorText(e) {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : 'The add-on could not be changed.';
}

export default function ProductAddonsSection({
  tenantId,
  headers,
  onChanged,
  canEdit = false,
  permissionReason = 'Requires the tenant:update permission. Ask an Owner if you need it.',
}) {
  const [products, setProducts] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [choice, setChoice] = useState({});
  const [saving, setSaving] = useState(null);
  const [result, setResult] = useState({});
  const [error, setError] = useState({});

  useEffect(() => {
    let live = true;
    axios.get(`${ADMIN_API}/tenants/${tenantId}/product-addons`, { headers })
      .then(({ data }) => { if (live) setProducts(data.products || []); })
      .catch((e) => { if (live) setLoadError(errorText(e)); });
    return () => { live = false; };
    // `headers` is rebuilt every render by the page; the tenant is the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  /* Re-read prices after the operator sets one in Base Prices. Only the
     server's `default_price` is taken: a staged choice or a result line on
     screen stays exactly as the operator left it. A failed re-read leaves the
     prices shown as they were and says so on the row it was asked from. */
  const [rechecking, setRechecking] = useState(false);
  const [recheckError, setRecheckError] = useState(null);
  const recheck = useCallback(async () => {
    setRechecking(true);
    setRecheckError(null);
    try {
      const { data } = await axios.get(
        `${ADMIN_API}/tenants/${tenantId}/product-addons`, { headers });
      const fresh = new Map((data.products || []).flatMap((p) => (p.addons || [])
        .map((a) => [a.code, a.default_price])));
      setProducts((list) => (list || []).map((p) => ({
        ...p,
        addons: (p.addons || []).map((a) => (fresh.has(a.code)
          ? { ...a, default_price: fresh.get(a.code) } : a)),
      })));
    } catch (e) {
      setRecheckError(errorText(e));
    } finally {
      setRechecking(false);
    }
    // `headers` is rebuilt every render by the page; the tenant is the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  if (loadError) {
    return (
      <p className="text-xs text-warning-text" data-testid="product-addons-error">
        Add-ons could not be loaded: {loadError}
      </p>
    );
  }
  if (!products || products.length === 0) return null;

  const apply = async (product, addon) => {
    if (!canEdit) return;
    const enabled = choice[addon.code];
    setSaving(addon.code);
    setError((x) => ({ ...x, [addon.code]: null }));
    try {
      const { data } = await axios.put(
        `${ADMIN_API}/tenants/${tenantId}/product-addons/${addon.code}`,
        { enabled }, { headers });
      setProducts((list) => list.map((p) => (p.product !== product.product ? p : {
        ...p,
        addons: p.addons.map((a) => (a.code === addon.code
          ? { ...a, enabled: data.enabled } : a)),
      })));
      setChoice((c) => ({ ...c, [addon.code]: undefined }));
      setResult((r) => ({ ...r, [addon.code]: data }));
      onChanged?.(data);
    } catch (e) {
      setError((x) => ({ ...x, [addon.code]: errorText(e) }));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="rounded-lg border border-border px-3 py-3 space-y-3"
         data-testid="product-addons-section">
      <p className="text-sm font-medium">Add-ons</p>
      {!canEdit && (
        <p className="text-xs text-muted-foreground" data-testid="product-addons-permission">
          {permissionReason}
        </p>
      )}
      {products.map((p) => (
        <div key={p.product} data-testid={`product-addons-${p.product}`}>
          <p className="text-sm">
            {p.label}
            {!p.held && (
              <span className="ml-2 text-xs text-muted-foreground"
                    data-testid={`addons-product-not-held-${p.product}`}>
                Not enabled for this tenant
              </span>
            )}
          </p>
          <IncludedFeatureTicks code={p.product} features={p.included_features}
                                productLabel={p.label} />
          <div className="mt-2 pl-6 space-y-2">
            {(p.addons || []).map((a) => {
              const pending = choice[a.code];
              const value = pending ?? !!a.enabled;
              const dirty = pending != null && pending !== !!a.enabled;
              const unknown = a.enabled === null || a.enabled === undefined;
              // ON needs the product; OFF is always allowed.
              const blocked = !p.held && !a.enabled;
              const done = result[a.code];
              const price = addonPriceText(a.default_price);
              return (
                <div key={a.code} data-testid={`addon-row-${a.code}`}>
                  <div className="flex items-center justify-between gap-3">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={value}
                             disabled={!canEdit || blocked || unknown || saving === a.code}
                             title={!canEdit ? permissionReason : undefined}
                             onChange={(e) => setChoice((c) => ({ ...c, [a.code]: e.target.checked }))}
                             data-testid={`addon-${a.code}`} />
                      {a.label}
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border
                                       bg-card text-muted-foreground border-input">
                        Optional add-on
                      </span>
                    </label>
                    {price && (
                      <span className={`text-xs ${a.default_price?.priced
                        ? 'text-foreground' : 'text-warning-text'}`}
                            data-testid={`addon-price-${a.code}`}>
                        {price}
                      </span>
                    )}
                  </div>
                  {a.description && (
                    <p className="mt-1 pl-6 text-xs text-muted-foreground">
                      {a.description}
                    </p>
                  )}
                  {a.retention_days != null && (
                    <p className="mt-1 pl-6 text-xs text-muted-foreground"
                       data-testid={`addon-retention-${a.code}`}>
                      Customer-visible activity history: {a.retention_days} days
                      {a.retention_policy === 'fixed' ? ' (fixed)' : ''}.
                    </p>
                  )}
                  {a.default_price && !a.default_price.priced && (
                    <div className="mt-1 pl-6">
                      <PriceRemedy remedy={a.default_price.remedy} onRecheck={recheck}
                                   rechecking={rechecking}
                                   testId={`addon-price-remedy-${a.code}`} />
                      {recheckError && (
                        <p className="text-xs text-warning-text"
                           data-testid={`addon-price-recheck-error-${a.code}`}>
                          The price could not be re-read: {recheckError}
                        </p>
                      )}
                    </div>
                  )}
                  {blocked && (
                    <p className="mt-1 pl-6 text-xs text-muted-foreground"
                       data-testid={`addon-needs-${a.code}`}>
                      Enable {p.label} for this tenant first, then add {a.label}.
                    </p>
                  )}
                  {unknown && (
                    <p className="mt-1 pl-6 text-xs text-warning-text"
                       data-testid={`addon-unknown-${a.code}`}>
                      This tenant's capability record could not be read, so {a.label} cannot
                      be shown as on or off.
                    </p>
                  )}
                  {dirty && value === false && (
                    <p className="mt-1 pl-6 text-xs text-warning-text"
                       data-testid={`addon-off-warning-${a.code}`}>
                      Turning {a.label} off removes access to it. Nothing is deleted.
                    </p>
                  )}
                  <div className="mt-1 pl-6 flex items-center gap-2">
                    <Button type="button" size="sm" variant="outline"
                            disabled={!canEdit || saving === a.code || (!dirty && (blocked || unknown))}
                            title={!canEdit ? permissionReason : undefined}
                            onClick={() => (dirty
                              ? apply(p, a)
                              : setChoice((c) => ({ ...c, [a.code]: !a.enabled })))}
                            data-testid={`apply-addon-${a.code}`}>
                      {actionLabel({ saving: saving === a.code, dirty, value,
                                     enabled: !!a.enabled, label: a.label })}
                    </Button>
                    {dirty && saving !== a.code && (
                      <Button type="button" size="sm" variant="ghost"
                              onClick={() => setChoice((c) => ({ ...c, [a.code]: undefined }))}
                              data-testid={`cancel-addon-${a.code}`}>
                        Cancel
                      </Button>
                    )}
                    {done?.changed && (
                      <span className="text-xs text-muted-foreground"
                            data-testid={`addon-result-${a.code}`}>
                        {done.message}
                        {done.agreement_amendment_required
                          ? ' The agreement is not re-priced automatically; amend it to bill this change.'
                          : ''}
                      </span>
                    )}
                  </div>
                  {error[a.code] && (
                    <p className="mt-1 pl-6 text-xs text-warning-text" role="alert"
                       data-testid={`addon-error-${a.code}`}>
                      {error[a.code]}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
